import { Injectable } from '@angular/core';

/**
 * Service to manage payment modal display logic.
 * Ensures modal shows only once per session to avoid spam while maintaining conversion.
 * 
 * Business Logic:
 * - Modal auto-opens when pathway becomes "paused" (after consultation validation)
 * - Shows only once per browser session (sessionStorage)
 * - Reappears after logout/login for better conversion rate
 * - Provides fallback banner as persistent reminder
 * 
 * @example
 * ```typescript
 * // Check if modal should show
 * if (this.paymentModalManager.shouldShowModal(pathwayId)) {
 *   this.openPaymentModal();
 * }
 * 
 * // Mark as dismissed when user closes modal
 * this.paymentModalManager.markModalDismissed(pathwayId);
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentModalManagerService {
  /** Storage key prefix for tracking dismissed modals */
  private readonly STORAGE_KEY_PREFIX = 'payment_modal_dismissed_';

  /**
   * Determines if payment modal should be shown for a pathway.
   * 
   * @param pathwayId - The pathway ID to check
   * @returns true if modal should be shown, false if already dismissed this session
   */
  public shouldShowModal(pathwayId: string): boolean {
    if (!pathwayId) return false;
    
    const storageKey = this.STORAGE_KEY_PREFIX + pathwayId;
    const dismissed = sessionStorage.getItem(storageKey);
    
    // Show modal only if not dismissed in current session
    return !dismissed;
  }

  /**
   * Marks a payment modal as dismissed for the current session.
   * 
   * @param pathwayId - The pathway ID to mark as dismissed
   */
  public markModalDismissed(pathwayId: string): void {
    if (!pathwayId) return;
    
    const storageKey = this.STORAGE_KEY_PREFIX + pathwayId;
    const timestamp = Date.now().toString();
    
    sessionStorage.setItem(storageKey, timestamp);
  }

  /**
   * Clears dismissal flags for a pathway.
   * Called when pathway becomes active again or payment is completed.
   * 
   * @param pathwayId - The pathway ID to clear flags for
   */
  public clearModalFlags(pathwayId: string): void {
    if (!pathwayId) return;
    
    const storageKey = this.STORAGE_KEY_PREFIX + pathwayId;
    sessionStorage.removeItem(storageKey);
  }

  /**
   * Clears all modal dismissal flags.
   * Useful for cleanup or testing purposes.
   */
  public clearAllFlags(): void {
    // Get all sessionStorage keys
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove matching keys
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }

  /**
   * Gets the timestamp when modal was dismissed for a pathway.
   * 
   * @param pathwayId - The pathway ID to check
   * @returns timestamp string or null if not dismissed
   */
  public getModalDismissedTimestamp(pathwayId: string): string | null {
    if (!pathwayId) return null;
    
    const storageKey = this.STORAGE_KEY_PREFIX + pathwayId;
    return sessionStorage.getItem(storageKey);
  }
}